<template>
  <o-modal :active="isActive" :canCancel="false" class="modal-sm">
    <div class="modal-card">
      <header class="modal-card-head">
        <h4 class="modal-card-title">
          Replace Category '<b>{{ categoryToDelete.name }}</b>'
        </h4>
      </header>
      <div class="modal-card-body is-visible">
        <div class="is-size-5 mb-3">
          '<b>{{ categoryToDelete.name }}</b>' is assigned to:
          <div class="content">
            <ol type="I">
              <li v-if="asmbCount">{{ asmbCount }} {{ asmbCount > 1 ? 'Assemblies' : 'Assembly' }}</li>
              <li v-if="ptCount">{{ ptCount }} {{ ptCount > 1 ? 'Parts' : 'Part' }}</li>
              <li v-if="vendorCount">{{ vendorCount }} {{ vendorCount > 1 ? 'Vendors' : 'Vendor' }}</li>
            </ol>
          </div>
          <div>
            If you'd like to assign the same categories for all of the above mentioned, please select
            them below. If not, follow the link to manually assign new categories to each Assembly or
            Part of Vendor.
          </div>
        </div>

        <div class="field">
          <div class="field-label">
            <label class="label is-size-3">Category</label>
          </div>
          <div class="field-body">
            <mf-multi-select
              v-model="newCategory"
              label="name"
              :options="categoriesToShow"
              :allow-empty="false"
              @update:modelValue="fetchSubCategories"
            />
          </div>
        </div>

        <div v-if="subCategoriesFetched" class="field">
          <div class="field-label">
            <label class="label is-size-3">Sub-Category</label>
          </div>
          <div class="field-body">
            <mf-multi-select
              v-model="newSubCategory"
              label="name"
              :options="subCategoriesToShow"
              :allow-empty="false"
            />
          </div>
        </div>
      </div>
      <footer class="modal-card-foot is-justify-content-flex-end">
        <button class="button is-outlined mr-2" @click="closeModal">Cancel</button>
        <button class="button has-background-black-bis has-text-white"
          @click="confirmArchive"
        >
          Replace
        </button>
      </footer>
      <o-loading :full-page="true" :active="isLoading" :can-cancel="false" />
    </div>
  </o-modal>
</template>

<script>
import { reactive, toRefs, onMounted } from 'vue';
import { useToast } from 'vue-toastification';
import { filter, get } from 'lodash';
import Category from '@/models/Vendors';

export default {
  name: 'ReplaceCategory',
  props: {
    isActive: {
      type: Boolean,
      default: false,
    },
    categoryToDelete: {
      type: Object,
      default: () => ({}),
    },
    ptCount: {
      type: Number,
      default: 0,
    },
    asmbCount: {
      type: Number,
      default: 0,
    },
    vendorCount: {
      type: Number,
      default: 0,
    },
  },
  emits: ['close-modal'],
  setup(props, { emit }) {
    const toast = useToast();

    const data = reactive({
      isLoading: false,
      categoriesToShow: [],
      subCategoriesToShow: [],
      subCategoriesFetched: false,
      newCategory: null,
      newSubCategory: null,
    });

    const fetchSubCategories = async () => {
      data.subCategoriesFetched = false;
      if (data.newCategory?._id) {
        try {
          const result = await Category.getCategories({
            isParent: false,
            categoryId: [data.newCategory._id],
            limit: 500,
          });
          data.subCategoriesToShow = result.data ?? [];
          data.newSubCategory = data.subCategoriesToShow.find((s) => s.isDefault) || null;
          data.subCategoriesFetched = true;
        } catch (e) {
          toast.error('Error loading sub-categories');
        }
      }
    };


    onMounted(async () => {
      if (!props.categoryToDelete?._id) return;
      try {
        const allCats = (await Category.getCategories({ limit: 99999 })).data;
        data.categoriesToShow = filter(allCats, (c) => c._id !== props.categoryToDelete._id);
        data.newCategory = data.categoriesToShow.find((c) => c.isDefault) || data.categoriesToShow[0];
        await fetchSubCategories();
      } catch (e) {
        toast.error('Error loading categories');
      }
    });

    const closeModal = () => {
      emit('close-modal');
    };

    const confirmArchive = async () => {
      data.isLoading = true;
      try {
        await Category.archiveCategory({
          categoryId: props.categoryToDelete._id,
          newCategoryId: get(data.newCategory, '_id', null),
          newSubCategoryId: get(data.newSubCategory, '_id', null),
          replace: true,
        });
        toast.success('Category replaced successfully');
        closeModal();
      } catch (e) {
        toast.error('Error while replacing category');
      } finally {
        data.isLoading = false;
      }
    };

    return {
      ...toRefs(data),
      confirmArchive,
      closeModal,
      fetchSubCategories,
    };
  },
};
</script>